import React, { ComponentType, createContext, PropsWithChildren, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Drawer,
  Fade,
  IconButton,
  Modal,
  Paper,
  Popover,
  PopoverOrigin,
  PopoverPosition,
  Stack,
  Typography
} from "@mui/material";
import { SimpleHeadMui } from "components/Simple-head/SimpleHeadMui";

type PromptSystemProps<T = unknown> = {
  onSubmit: (result: T) => void;
  onClose?: () => void;
  onCancel?: () => void;
  onBack?: () => void;
  isMinimized?: boolean;
};

type PromptComponentType = "modal" | "popover" | "drawer" | "fullscreen" | "status";

type ComponentCustomProps<ComponentProps> = Omit<ComponentProps, keyof PromptSystemProps<any>>;

type PromptStateWithProps<
  ComponentProps,
  HardReject extends boolean = false,
  T extends PromptComponentType = "modal"
> = {
  Component: ComponentType<
    ComponentProps & PromptSystemProps<ComponentProps extends { onSubmit: (result: infer R) => void } ? R : any>
  >;
  componentType: T;
  props: ComponentCustomProps<ComponentProps>;
  anchorEl?: T extends "popover" ? HTMLElement : HTMLElement | null;
  anchorPosition?: T extends "popover" ? PopoverPosition : never;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  anchor?: T extends "drawer" ? "left" | "right" | "top" | "bottom" : never;
  drawerHeader?: string;
  hideBackdrop?: boolean;
  hardReject?: HardReject;
  resolve: (value: ComponentProps extends { onSubmit: (result: infer R) => void } ? R : any) => void;
  reject: (reason?: any) => void;
  disableBackdropClose?: boolean;
  statusTitle?: T extends "status" ? string : never;
  statusDefaultMinimized?: T extends "status" ? boolean : never;
};

type PromptStateWithoutProps<
  ComponentProps,
  HardReject extends boolean = false,
  T extends PromptComponentType = "modal"
> = Omit<PromptStateWithProps<ComponentProps, HardReject, T>, "props">;

type PromptState<ComponentProps, HardReject extends boolean = false, T extends PromptComponentType = "modal"> =
  ComponentCustomProps<ComponentProps> extends Record<string, never>
    ? PromptStateWithoutProps<ComponentProps, HardReject, T>
    : PromptStateWithProps<ComponentProps, HardReject, T>;

type OpenPromptParams<ComponentProps, HardReject extends boolean = false, T extends PromptComponentType = "modal"> = {
  Component: ComponentType<
    ComponentProps & PromptSystemProps<ComponentProps extends { onSubmit: (result: infer R) => void } ? R : any>
  >;
  componentType?: T;
  hardReject?: HardReject;
  disableBackdropClose?: boolean;
} & (ComponentCustomProps<ComponentProps> extends Record<string, never>
  ? {}
  : { props: ComponentCustomProps<ComponentProps> }) &
  (T extends "popover"
    ? ({ anchorEl: HTMLElement; anchorPosition?: never } | { anchorEl?: never; anchorPosition: PopoverPosition }) & {
        anchorOrigin?: PopoverOrigin;
        transformOrigin?: PopoverOrigin;
        hideBackdrop?: boolean;
      }
    : {}) &
  (T extends "drawer"
    ? { anchor?: "left" | "right" | "top" | "bottom"; drawerHeader?: string; hideBackdrop?: boolean }
    : {}) &
  (T extends "status" ? { statusTitle: string; statusDefaultMinimized?: boolean } : {});

export type PromptType = <
  ComponentProps = any,
  HardReject extends boolean = false,
  T extends PromptComponentType = "modal"
>(
  data: OpenPromptParams<ComponentProps, HardReject, T>
) => Promise<
  ComponentProps extends { onSubmit: (result: infer R) => void } ? (HardReject extends true ? R : R | null) : any
>;

export const PromptContext = createContext<PromptType | null>(null);

export const PromptProvider: React.FC<PropsWithChildren> = ({ children }): JSX.Element => {
  const [promptState, setPromptState] = useState<Record<number, PromptState<any, boolean, PromptComponentType>>>({});

  function openPrompt<ComponentProps, HardReject extends boolean = false, T extends PromptComponentType = "modal">(
    data: OpenPromptParams<ComponentProps, HardReject, T>
  ): Promise<
    ComponentProps extends { onSubmit: (result: infer R) => void } ? (HardReject extends true ? R : R | null) : any
  > {
    return new Promise((resolve, reject) => {
      const state = {
        Component: data.Component,
        componentType: data.componentType || "modal",
        hardReject: data.hardReject,
        resolve,
        reject,
        ...("props" in data && { props: data.props }),
        ...("anchorEl" in data && { anchorEl: data.anchorEl }),
        ...("anchorPosition" in data && { anchorPosition: data.anchorPosition }),
        ...("anchorOrigin" in data && { anchorOrigin: data.anchorOrigin }),
        ...("transformOrigin" in data && { transformOrigin: data.transformOrigin }),
        ...("anchor" in data && { anchor: data.anchor }),
        ...("drawerHeader" in data && { drawerHeader: data.drawerHeader }),
        ...("hideBackdrop" in data && { hideBackdrop: data.hideBackdrop }),
        ...("disableBackdropClose" in data && { disableBackdropClose: data.disableBackdropClose }),
        ...("statusTitle" in data && { statusTitle: data.statusTitle }),
        ...("statusDefaultMinimized" in data && { statusDefaultMinimized: data.statusDefaultMinimized })
      } as PromptState<ComponentProps, HardReject, T>;

      setPromptState((prevState) => ({
        ...prevState,
        [Date.now()]: state as PromptState<any, boolean, T>
      }));
    });
  }

  const closePrompt = (openTimestamp: number, result: any, isError = false) => {
    if (isError) {
      promptState?.[openTimestamp]?.reject(result);
    } else {
      promptState?.[openTimestamp]?.resolve(result);
    }
    setPromptState((prevState) => {
      const prevCopy = { ...prevState };
      delete prevCopy[openTimestamp];
      return prevCopy;
    });
  };

  const handleClose = (openTimestamp: number) => {
    if (promptState?.[openTimestamp]?.hardReject) {
      promptState?.[openTimestamp]?.reject(new PromptCancelError("canceled"));
    } else {
      promptState?.[openTimestamp]?.resolve(null);
    }
    setPromptState((prevState) => {
      const prevCopy = { ...prevState };
      delete prevCopy[openTimestamp];
      return prevCopy;
    });
  };

  const handleBack = (openTimestamp: number) => {
    promptState?.[openTimestamp]?.reject(new PromptCancelError("back"));
    setPromptState((prevState) => {
      const prevCopy = { ...prevState };
      delete prevCopy[openTimestamp];
      return prevCopy;
    });
  };

  const allEntries = Object.entries(promptState);
  const statusEntries = allEntries.filter(([, s]) => s.componentType === "status");
  const otherEntries = allEntries.filter(([, s]) => s.componentType !== "status");

  return (
    <PromptContext.Provider value={openPrompt}>
      {children}
      {Boolean(statusEntries.length) && (
        <Stack
          direction="column-reverse"
          gap="12px"
          sx={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            zIndex: 1000,
            maxWidth: "calc(100vw - 48px)",
            pointerEvents: "none"
          }}
        >
          {statusEntries.map(([openTimestamp, state]) => (
            <StatusPanel
              key={openTimestamp}
              title={state.statusTitle || ""}
              defaultMinimized={Boolean(state.statusDefaultMinimized)}
              onClose={() => handleClose(Number(openTimestamp))}
              renderChild={(isMinimized: boolean) => (
                <state.Component
                  {...state.props}
                  isMinimized={isMinimized}
                  onSubmit={(result: any) => closePrompt(Number(openTimestamp), result)}
                  onCancel={() => handleClose(Number(openTimestamp))}
                />
              )}
            />
          ))}
        </Stack>
      )}
      {otherEntries.map(([openTimestamp, state]) => {
        if (state.componentType === "popover") {
          return (
            <Popover
              key={openTimestamp}
              anchorEl={state.anchorEl}
              anchorReference={state.anchorPosition ? "anchorPosition" : "anchorEl"}
              anchorPosition={state.anchorPosition}
              anchorOrigin={state.anchorOrigin}
              transformOrigin={state.transformOrigin}
              open={Boolean(state)}
              hideBackdrop={state.hideBackdrop}
              disableEscapeKeyDown={state.disableBackdropClose}
              disableEnforceFocus={state.hideBackdrop}
              disableAutoFocus={state.hideBackdrop}
              disableRestoreFocus={state.hideBackdrop}
              {...(state.hideBackdrop && { TransitionComponent: Fade })}
              sx={state.hideBackdrop ? { pointerEvents: "none" } : undefined}
              onClose={(_e, reason) => {
                if (state.disableBackdropClose && reason === "backdropClick") return;
                handleClose(Number(openTimestamp));
              }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: "8px",
                    ...(state.hideBackdrop && {
                      pointerEvents: "auto",
                      background: "transparent",
                      boxShadow: "none",
                      overflow: "visible"
                    })
                  }
                }
              }}
            >
              <state.Component
                {...state.props}
                onSubmit={(result: any) => closePrompt(Number(openTimestamp), result)}
                onCancel={() => handleClose(Number(openTimestamp))}
              />
            </Popover>
          );
        }

        if (state.componentType === "modal") {
          return (
            <Modal
              key={openTimestamp}
              open
              onClose={(_e, reason) => {
                if (state.disableBackdropClose && reason === "backdropClick") return;
                handleClose(Number(openTimestamp));
              }}
            >
              <Box sx={modalStyle}>
                <state.Component
                  {...state.props}
                  onSubmit={(result: unknown) => closePrompt(Number(openTimestamp), result)}
                  onCancel={() => handleClose(Number(openTimestamp))}
                  onBack={() => handleBack(Number(openTimestamp))}
                />
              </Box>
            </Modal>
          );
        }

        if (state.componentType === "drawer") {
          return (
            <Drawer
              key={openTimestamp}
              anchor={state.anchor || "right"}
              open={Boolean(state)}
              onClose={(_e, reason) => {
                if (state.disableBackdropClose && reason === "backdropClick") return;
                handleClose(Number(openTimestamp));
              }}
              {...(state.hideBackdrop && { slotProps: { backdrop: { style: { backgroundColor: "transparent" } } } })}
              sx={{
                ".MuiPaper-root":
                  state.anchor === "right"
                    ? {
                        borderTopLeftRadius: "25px",
                        borderBottomLeftRadius: "25px"
                      }
                    : {
                        borderTopRightRadius: "25px",
                        borderBottomRightRadius: "25px"
                      }
              }}
            >
              <Stack alignItems="stretch" height="100%" bgcolor="#ffffff" overflow="hidden">
                <SimpleHeadMui onIconClick={() => handleClose(Number(openTimestamp))}>
                  {state.drawerHeader}
                </SimpleHeadMui>
                <Stack overflow="auto">
                  <state.Component
                    {...state.props}
                    onSubmit={(result: unknown) => closePrompt(Number(openTimestamp), result)}
                    onCancel={() => handleClose(Number(openTimestamp))}
                    onBack={() => handleBack(Number(openTimestamp))}
                  />
                </Stack>
              </Stack>
            </Drawer>
          );
        }

        if (state.componentType === "fullscreen") {
          return (
            <Drawer
              key={openTimestamp}
              anchor="bottom"
              open={Boolean(state)}
              onClose={(_e, reason) => {
                if (state.disableBackdropClose && reason === "backdropClick") return;
                handleClose(Number(openTimestamp));
              }}
              transitionDuration={{ enter: 250, exit: 250 }}
              SlideProps={{ appear: true }}
              ModalProps={{ disableRestoreFocus: true }}
              sx={{
                ".MuiPaper-root": {
                  width: "100vw",
                  height: "100vh",
                  borderRadius: 0
                }
              }}
            >
              <state.Component
                {...state.props}
                onSubmit={(result: unknown) => closePrompt(Number(openTimestamp), result)}
                onCancel={() => handleClose(Number(openTimestamp))}
                onBack={() => handleBack(Number(openTimestamp))}
              />
            </Drawer>
          );
        }
        return null;
      })}
    </PromptContext.Provider>
  );
};

export class PromptCancelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PromptCancelError";
  }
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 8
  // boxShadow: 24
};

interface StatusPanelProps {
  title: string;
  defaultMinimized?: boolean;
  onClose: () => void;
  renderChild: (isMinimized: boolean) => React.ReactNode;
}

const StatusPanel: React.FC<StatusPanelProps> = ({ title, defaultMinimized, onClose, renderChild }) => {
  const [minimized, setMinimized] = useState<boolean>(Boolean(defaultMinimized));

  return (
    <Paper
      elevation={6}
      sx={{
        width: minimized ? 280 : 360,
        borderRadius: "12px",
        overflow: "hidden",
        pointerEvents: "auto",
        bgcolor: "#ffffff",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
        transition: "width 200ms ease"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setMinimized((v) => !v)}
        sx={{
          px: "16px",
          py: "10px",
          cursor: "pointer",
          borderBottom: minimized ? "none" : "1px solid #DAE0E2",
          userSelect: "none",
          gap: "8px"
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexGrow: 1
          }}
        >
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" gap="4px">
          <IconButton
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setMinimized((v) => !v);
            }}
            aria-label={minimized ? "Expand" : "Collapse"}
          >
            {minimized ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <IconButton
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Box>{renderChild(minimized)}</Box>
    </Paper>
  );
};
